import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaUserCircle, FaSignOutAlt, FaBars, FaTimes } from "react-icons/fa";
import Swal from "sweetalert2";
import "./Sidebar.css";
import A2ZLogo from "../../Shared/Images/A2Zlogo.jpeg";
import { API_BASE } from "../../../ApiUrl";

const MENU_ITEMS = [
  {
    label: "User Profile",
    icon: FaUserCircle,
    to: "/profile",
    paths: ["/profile"],
  },
];

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const isActive = (paths) => {
    const pathArray = Array.isArray(paths) ? paths : [paths];
    return pathArray.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  useEffect(() => {
    document.body.classList.toggle("ta-sidebar-collapsed", isCollapsed);
    return () => document.body.classList.remove("ta-sidebar-collapsed");
  }, [isCollapsed]);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  const toggleSidebar = () => setIsCollapsed((prev) => !prev);
  const toggleMobileSidebar = () => setIsMobileOpen((prev) => !prev);

  const fetchUserDetails = async (userId) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("admin_token");

      const response = await fetch(`${API_BASE}/users/${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user details");
      }

      const result = await response.json();

      if (result.success && result.data) {
        setUserDetails(result.data);
      } else {
        setUserDetails(result);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);

      const adminData = localStorage.getItem("tenant_user");

      if (adminData) {
        try {
          const parsedUser = JSON.parse(adminData);
          setUserDetails({
            full_name: parsedUser.full_name || "User",
            email: parsedUser.email || "user@example.com",
            role: parsedUser.role || "User",
          });
        } catch (e) {
          console.error("Error parsing user data:", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let adminData = localStorage.getItem("tenant_user");

    if (!adminData) {
      adminData = localStorage.getItem("admin_user");
    }

    if (adminData) {
      try {
        const parsedUser = JSON.parse(adminData);
        setUser(parsedUser);

        if (parsedUser.user_id) {
          fetchUserDetails(parsedUser.user_id);
        } else {
          setUserDetails({
            full_name: parsedUser.full_name || parsedUser.identifier || "User",
            email:
              parsedUser.email || parsedUser.identifier || "user@example.com",
            role: parsedUser.role || "User",
          });
          setLoading(false);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of the system",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("tenant_user");
        localStorage.removeItem("admin_user");
        localStorage.removeItem("token");
        localStorage.removeItem("admin_token");

        Swal.fire({
          icon: "success",
          title: "Logged Out!",
          text: "You have been successfully logged out.",
          timer: 1500,
          showConfirmButton: false,
        });

        navigate("/Oceanstar/Login");
      }
    });
  };

  const getDisplayName = () => {
    if (loading) return "Loading...";

    if (userDetails?.full_name) {
      return userDetails.full_name;
    }

    if (userDetails?.first_name && userDetails?.last_name) {
      return `${userDetails.first_name} ${userDetails.last_name}`.trim();
    }

    if (userDetails?.first_name) {
      return userDetails.first_name;
    }

    if (user?.full_name) {
      return user.full_name;
    }

    if (user?.identifier) {
      return user.identifier.split("@")[0];
    }

    const email = getDisplayEmail();

    if (email && email !== "user@example.com") {
      return email.split("@")[0];
    }

    return "User";
  };

  const getDisplayEmail = () => {
    if (loading) return "Loading...";

    if (userDetails?.email) {
      return userDetails.email;
    }

    if (user?.email) {
      return user.email;
    }

    if (user?.identifier && user.identifier.includes("@")) {
      return user.identifier;
    }

    return "user@example.com";
  };

  const getUserInitials = () => {
    if (loading) return "..";

    const name = getDisplayName();

    if (
      name &&
      name !== "User" &&
      name !== "Loading..." &&
      name !== "user@example.com"
    ) {
      const nameParts = name.split(" ");

      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }

      return name.substring(0, 2).toUpperCase();
    }

    const email = getDisplayEmail();

    if (email && email !== "user@example.com") {
      return email.substring(0, 2).toUpperCase();
    }

    return "";
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`ta-mobile-overlay ${isMobileOpen ? "show" : ""}`}
        onClick={toggleMobileSidebar}
      />

      <aside
        className={`ta-sidebar ${isCollapsed ? "collapsed" : ""} ${
          isMobileOpen ? "mobile-open" : ""
        }`}
      >
        {/* Header */}
        <div className="ta-sidebar-header">
          <div className="ta-logo-icon">
            <img src={A2ZLogo} alt="A2Z Logo" />
          </div>

          <div className="ta-sidebar-header-text">
            <h6>Oceanstar User</h6>
            <small>Surveyor Management</small>
          </div>

          <button
            className="ta-sidebar-collapse-btn"
            onClick={toggleSidebar}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-label="Toggle sidebar"
          >
            <FaBars />
          </button>
        </div>

        {/* Scrollable Menu */}
        <nav className="ta-sidebar-scroll">
          {MENU_ITEMS.map(({ label, icon: Icon, to, paths }) => (
            <div className="ta-menu-section" key={to}>
              <Link
                to={to}
                title={label}
                className={`ta-menu-title ${
                  isActive(paths) ? "active" : ""
                }`}
              >
                <span className="ta-menu-icon">
                  <Icon />
                </span>

                <span className="ta-menu-label">{label}</span>
              </Link>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="ta-sidebar-footer">
          <div className="ta-user-info-wrapper">
            <div className="ta-avatar" title={getDisplayName()}>
              {getUserInitials()}
            </div>

            <div className="ta-user-details">
              <p className="ta-sidebar-user-name">
                {getDisplayName()}
              </p>

              <span className="ta-user-email">
                {getDisplayEmail()}
              </span>
            </div>

            <button
              className="ta-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <FaSignOutAlt />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Toggle */}
      <button
        className="ta-mobile-toggle"
        onClick={toggleMobileSidebar}
      >
        {isMobileOpen ? <FaTimes /> : <FaBars />}
      </button>
    </>
  );
};

export default Sidebar;